<div class="max-w-[1180px] mx-auto px-[16px] md:px-[40px] pt-[16px] pb-[100px] flex flex-col gap-4">
  <!-- One set of questions per content language. The switcher shows which
       languages this tenant has filled in, so a missing one is obvious. -->
  <div class="flex flex-wrap items-center justify-between gap-3">
    <div class="flex items-center gap-2">
      @for (row of languages; track row.language) {
        <button
          type="button"
          (click)="selectLanguage(row.language)"
          class="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border text-[14px] font-medium transition-colors"
          [ngClass]="
            language === row.language
              ? '!border-[var(--primary-color)] bg-[color-mix(in_srgb,var(--primary-color)_7%,white)] text-slate-900'
              : 'border-slate-200 bg-white text-slate-600 hover:!border-slate-300'
          "
        >
          {{ languageLabel(row.language) }}
          <!-- Ready only once every field is filled in: until then the copy is
               stored but never shown to users. -->
          <span
            class="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
            [ngClass]="
              row.isComplete
                ? 'bg-[color-mix(in_srgb,var(--primary-color)_12%,white)] text-[var(--primary-color)]'
                : row.hasContent
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-500'
            "
          >
            {{
              (row.isComplete
                ? "questionnaireReady"
                : row.hasContent
                  ? "questionnaireIncomplete"
                  : "questionnaireMissing"
              ) | transloco
            }}
          </span>
        </button>
      }
    </div>

    <div class="flex items-center gap-2">
      <!-- Load Default and Clear only touch the form, so the state on screen
           can differ from what is stored until Save runs. -->
      @if (form.dirty) {
        <span class="text-[12px] text-amber-700 mr-1">
          {{ "unsavedChanges" | transloco }}
        </span>
      }
      <button
        type="button"
        (click)="loadDefaults()"
        [disabled]="isSaving || isLoading || isLoadingDefaults"
        class="px-2.5 py-1.5 rounded-lg border !border-slate-200 bg-white text-slate-600 text-[14px] font-medium hover:!bg-slate-50 transition disabled:opacity-40"
      >
        {{ "loadDefault" | transloco }}
      </button>
      <button
        type="button"
        (click)="clear()"
        [disabled]="isSaving || isLoading || isLoadingDefaults"
        class="px-2.5 py-1.5 rounded-lg border !border-slate-200 bg-white text-slate-600 text-[14px] font-medium hover:!bg-slate-50 transition disabled:opacity-40"
      >
        {{ "clear" | transloco }}
      </button>
      <button
        type="button"
        (click)="save()"
        [disabled]="isSaving || isLoading"
        class="px-2.5 py-1.5 rounded-lg bg-[var(--primary-color)] hover:bg-[color-mix(in_srgb,var(--primary-color)_95%,black)] text-white text-[14px] font-medium transition disabled:opacity-40"
      >
        {{ "save" | transloco }}
      </button>
    </div>
  </div>

  @if (!isLoading && !isCurrentComplete) {
    <div
      class="rounded-xl border !border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800"
    >
      {{
        (hasContent ? "questionnaireIncompleteHint" : "questionnaireNoContentHint")
          | transloco
      }}
    </div>
  }

  @if (isLoading) {
    <div class="text-[14px] text-slate-500 py-6">
      {{ "loading" | transloco }}
    </div>
  } @else {
    <form [formGroup]="form" class="flex flex-col gap-4">
      <!-- Page copy around the questions: the intro card on the left and the
           navigation labels at the bottom of the questionnaire. -->
      <section class="rounded-2xl border !border-slate-200/70 bg-white p-5">
        <h2 class="text-[16px] font-semibold text-slate-900">
          {{ "questionnairePageCopy" | transloco }}
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireEyebrow" | transloco }}</span>
            <input formControlName="eyebrow" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireHeading" | transloco }}</span>
            <input formControlName="heading" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5 md:col-span-2">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireIntro" | transloco }}</span>
            <textarea formControlName="introText" rows="2" class="igntr-input resize-none"></textarea>
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireDuration" | transloco }}</span>
            <input formControlName="duration" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireCardTitle" | transloco }}</span>
            <input formControlName="cardTitle" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5 md:col-span-2">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireCardSubtitle" | transloco }}</span>
            <input formControlName="cardSubtitle" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5 md:col-span-2">
            <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireCounter" | transloco }}</span>
            <input formControlName="questionCounterTemplate" class="igntr-input" />
            <!-- Placeholders are substituted at render time; without them the
                 counter would show the same text on every question. -->
            <span class="text-[12px] text-slate-400">{{ "questionnaireCounterHint" | transloco }}</span>
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "back" | transloco }}</span>
            <input formControlName="backLabel" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "next" | transloco }}</span>
            <input formControlName="nextLabel" class="igntr-input" />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-[13px] font-medium text-slate-600">{{ "finish" | transloco }}</span>
            <input formControlName="finishLabel" class="igntr-input" />
          </label>
        </div>
      </section>

      <section class="rounded-2xl border !border-slate-200/70 bg-white p-5">
        <h2 class="text-[16px] font-semibold text-slate-900">
          {{ "questionnaireSteps" | transloco }}
        </h2>

        <div formArrayName="steps" class="flex flex-col gap-4 mt-4">
          @for (step of steps.controls; track $index; let i = $index) {
            <div [formGroupName]="i" class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label class="flex flex-col gap-1.5">
                <span class="text-[13px] font-medium text-slate-600">
                  {{ "questionnaireStep" | transloco }} {{ i + 1 }}
                </span>
                <input formControlName="title" class="igntr-input" />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-[13px] font-medium text-slate-600">{{ "description" | transloco }}</span>
                <input formControlName="sub" class="igntr-input" />
              </label>
            </div>
          }
        </div>
      </section>

      <!-- The six questions are fixed: their count, their option keys and the
           methodology tag each option maps to drive the scoring, so only the
           wording is editable here. The tag is shown next to each option so the
           admin can see what an answer actually selects. -->
      <div formArrayName="questions" class="flex flex-col gap-4">
        @for (question of questions.controls; track $index; let qi = $index) {
          <section
            [formGroupName]="qi"
            class="rounded-2xl border !border-slate-200/70 bg-white p-5"
          >
            <div class="flex items-center gap-2">
              <span
                class="shrink-0 w-6 h-6 rounded-md bg-[var(--primary-color)] text-white text-[13px] font-semibold flex items-center justify-center"
              >
                {{ question.get("number")?.value }}
              </span>
              <h2 class="text-[16px] font-semibold text-slate-900">
                {{ "question" | transloco }} {{ question.get("number")?.value }}
              </h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <label class="flex flex-col gap-1.5 md:col-span-2">
                <span class="text-[13px] font-medium text-slate-600">{{ "title" | transloco }}</span>
                <input formControlName="title" class="igntr-input" />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireHint" | transloco }}</span>
                <input formControlName="hint" class="igntr-input" />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireNote" | transloco }}</span>
                <input formControlName="note" class="igntr-input" />
              </label>

              @if (hasFreeText(question.get("number")?.value)) {
                <label class="flex flex-col gap-1.5">
                  <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireFreeTextLabel" | transloco }}</span>
                  <input formControlName="freeTextLabel" class="igntr-input" />
                </label>
                <label class="flex flex-col gap-1.5">
                  <span class="text-[13px] font-medium text-slate-600">{{ "questionnaireFreeTextPlaceholder" | transloco }}</span>
                  <input formControlName="freeTextPlaceholder" class="igntr-input" />
                </label>
              }
            </div>

            <div formArrayName="options" class="flex flex-col gap-2.5 mt-4">
              @for (option of options(question).controls; track $index; let oi = $index) {
                <div [formGroupName]="oi" class="flex items-start gap-3">
                  <span
                    class="shrink-0 w-6 h-6 mt-2 rounded-md bg-slate-100 text-slate-500 text-[12px] font-semibold flex items-center justify-center"
                  >
                    {{ option.get("optionKey")?.value }}
                  </span>
                  <div class="flex-1 flex flex-col gap-1">
                    <input formControlName="label" class="igntr-input" />
                    <span class="text-[12px] text-slate-400">
                      {{ "questionnaireTag" | transloco }}:
                      {{
                        tagFor(
                          question.get("number")?.value,
                          option.get("optionKey")?.value
                        )
                      }}
                    </span>
                  </div>
                </div>
              }
            </div>
          </section>
        }
      </div>
    </form>
  }
</div>
